import { dayLabel, daysLabel } from "@beacon/core/days"
import { formatOf, type DailyChartInput } from "./input.ts"

/** A chart's values as rows of text, for a reader who can't see the chart or needs the figures. */
export type ChartTable = {
  columns: string[]
  /**
   * A row a day, in the chart's order: the day, each series' value, then the day's total where
   * there are several series; or a row for a run of days whose history isn't known (`known`
   * false), keyed by its first day.
   */
  rows: { day: string; known: boolean; cells: string[] }[]
}

const notKnown = "Not known"

/**
 * The chart in a sentence, for its accessible description and a printed caption: the range, its
 * total, its busiest day, and how many days aren't known.
 */
export function summaryOf<K extends string>(input: DailyChartInput<K>) {
  const { series, days } = input
  const format = formatOf(input)
  const totals = days.flatMap(({ day, values }) =>
    values ? [{ day, total: series.reduce((sum, { key }) => sum + values[key], 0) }] : [],
  )
  const busiest = totals.reduce<(typeof totals)[number] | undefined>(
    (most, day) => (most && most.total >= day.total ? most : day),
    undefined,
  )
  const unknown = days.length - totals.length
  const range = `${dayLabel(days[0]?.day ?? "")} to ${dayLabel(days.at(-1)?.day ?? "")} (UTC)`
  const sum = format(totals.reduce((all, { total }) => all + total, 0))
  return [
    `${range}: ${sum} in all`,
    busiest ? `, the most on ${dayLabel(busiest.day)} (${format(busiest.total)}).` : ".",
    unknown === 0 ? "" : unknown === 1 ? " 1 day isn't known." : ` ${unknown} days aren't known.`,
  ].join("")
}

/**
 * The chart's data as a table: the web app shows it beside the chart and a PDF can print it. A day
 * whose history isn't known says so in every cell rather than 0, and a run of them is one row over
 * the days it runs. A single series has no total column, which would only repeat it.
 */
export function chartTableOf<K extends string>(input: DailyChartInput<K>): ChartTable {
  const { series, days } = input
  const format = formatOf(input)
  const totalled = series.length > 1
  const rows: ChartTable["rows"] = []
  let run: { first: string; last: string } | undefined
  const endRun = () => {
    if (!run) return
    const blank = Array.from({ length: series.length + (totalled ? 1 : 0) }, () => notKnown)
    rows.push({ day: run.first, known: false, cells: [daysLabel(run.first, run.last), ...blank] })
    run = undefined
  }
  for (const { day, values } of days) {
    if (!values) {
      run = { first: run?.first ?? day, last: day }
      continue
    }
    endRun()
    const total = series.reduce((sum, { key }) => sum + values[key], 0)
    rows.push({
      day,
      known: true,
      cells: [
        dayLabel(day),
        ...series.map(({ key }) => format(values[key])),
        ...(totalled ? [format(total)] : []),
      ],
    })
  }
  endRun()
  return {
    columns: ["Day (UTC)", ...series.map(({ label }) => label), ...(totalled ? ["Total"] : [])],
    rows,
  }
}
